import { type LucideIcon } from "lucide-react";

import { recordNavRoute, type NavRecordKey } from "@/lib/nav-records";
import { APP_ROUTES, isRouteAuthorized, type AppRoute } from "@/lib/routes";
import type { SessionFeatureFlags } from "@/types/generated/SessionFeatureFlags";
import type { ComponentType, ReactNode } from "react";

/**
 * The module registry — one declaration per module, replacing the per-module
 * `*_ROUTES` array + `get*NavSections` + `*Sidebar` + shell-boolean quartet.
 *
 * **Why a registry instead of more booleans.** `AppRoute` used to carry
 * `bare` / `globalShell` / `reportsShell` — one boolean per shell — which
 * `router.tsx` stamped onto every route's `handle` and `AppShell` read back
 * as a ternary cascade. That shape doesn't compose: each new module needed a
 * 4th and 5th boolean, another `matches.some()` extractor, another cascade
 * branch, another near-verbatim sidebar, and another copy of a ~40-line
 * nav-sections function. Adding one module touched 8 files. A single `moduleKey`
 * plus this registry makes it one entry here.
 *
 * **Modules don't own records.** Clients, Accounts, and Deals are
 * shared nav modules (`lib/nav-records.tsx`) that each module lists by key; the
 * record itself has one canonical module-neutral url
 * (`lib/record-paths.ts`) and renders inside whichever module you're in. This is
 * the Salesforce (module = a selection of tabs) / Dynamics (sitemap subareas
 * over shared Dataverse tables) shape, and it replaces the old "Global module"
 * that users had to travel to in order to reach a client.
 *
 * **Section keys must stay globally unique across modules** — `routeMetaFor`
 * resolves a breadcrumb's section heading from one merged label map, so two
 * modules using the same key must agree on its label. Prefix per module when in
 * doubt (`ops-workspace`, not `workspace`).
 */

/**
 * Stable mod id. Doubles as `AppRoute.moduleKey` and the ModuleSwitcher's key.
 *
 * Only built modules appear here. A module that's a launcher tile but has no
 * routes yet (Energy Insights, Budget Planning, …) lives in
 * `lib/launcher-modules.tsx` only, and gains a key here when it's built.
 */
export type ModuleKey =
  | "energy-advisor"
  | "operations"
  | "reports"
  | "admin"
  | "market-intelligence";

export interface ModuleDefinition {
  key: ModuleKey;
  label: string;
  icon: LucideIcon;
  /**
   * One-line blurb for the Modules picker. Optional — a module that also has a
   * `LAUNCHER_MODULES` tile inherits that tile's description, so only modules
   * without one (Admin, which isn't a launcher tile) need to set it here.
   */
  description?: string;
  /** Where the launcher / module switcher lands you. */
  entryPath: string;
  /** Optional authorized fallback for a workspace whose landing is hidden from the sidebar. */
  fallbackEntryPath?: string;
  /**
   * The module's ACCESS permission — `market_intelligence.access`, say. Off
   * hides the module from the switcher, the launcher and every sidebar, and
   * bounces its pages; the server refuses the module's dedicated API on the
   * same key. The module's own detailed permissions still apply on top.
   *
   * Required, so a new module cannot ship ungated. The keys are the Rust
   * catalog's `module_access` permissions (`permissions/catalog.rs`), one per
   * module; a module added there needs one here, and vice versa.
   */
  requiredPermission: string;
  /**
   * Also open to a platform super admin who lacks `requiredPermission` in the
   * tenant they are signed in to (AB#8687). Set it only on a module whose
   * super-admin pages manage every tenant, so a Pilot super admin signed in
   * to a partner tenant can still reach them. Inside the module each page
   * keeps its own gate.
   */
  openToSuperAdmins?: boolean;
  /**
   * Ordered sidebar sections. Each is a header plus the entries beneath it:
   * `modules` pulls in shared record modules by key; `routes` are the module's
   * own pages.
   */
  sections: ModuleSection[];
  /**
   * Routes the module registers with the router but keeps out of its sidebar —
   * detail pages, drilldowns, redirects. Shared-module detail routes are NOT
   * listed here; they register once globally (see `recordDetailRoutes`).
   */
  hiddenRoutes?: AppRoute[];
  /**
   * Extra content rendered in the sidebar header, beneath the module switcher
   * and above any `recordScope` pickers.
   *
   * A plain component reference (not a `ReactNode`) because it may need its
   * own hooks — Market Intelligence's Power/Gas toggle reads `useLocation` to
   * derive which commodity is selected. `ModuleSidebar` calls it at one fixed
   * position, so the hook count stays stable across renders regardless of
   * which module is active — the same reasoning as `recordScope.useScope`
   * being invoked from its own component rather than inline.
   *
   * Optional and unset by every module but Market Intelligence today — a
   * plain nav list needs nothing here.
   */
  sidebarHeader?: () => ReactNode;
  /**
   * A control in the top bar, beside the notifications bell, shown only while
   * this module is active — Market Intelligence's "My views" bookmark, which
   * opens `sidePanel`. A component, so it owns its hooks. Optional and unset
   * by every other module.
   */
  topbarAction?: ComponentType;
  /**
   * A column at the right of the page, under the top bar, that pushes the
   * page left — Market Intelligence's "My views" list. It decides for itself
   * when to show (it renders `null` when closed). Optional and unset by every
   * other module.
   */
  sidePanel?: ComponentType;
  /**
   * Show the shared record bar (Accounts, Clients, Deals) under the top bar
   * while this module is active. Energy Advisor and Operations set it: their
   * users work those records every day. Market Intelligence, Reports and
   * Admin leave it unset, because the strip sat above pages that never use
   * those records (AB#8790).
   *
   * A module opts in, so a new module gets no record bar until it sets this.
   * The record routes stay registered either way: links from search, Maxwell
   * and report rows still open `/clients` in whichever module you're in.
   */
  recordBar?: boolean;
  /**
   * Switches that live in the footer gear's popover itself, beneath any
   * settings pages, for a setting too small to earn a page — Reports' "Enable
   * Emails" in a lower environment. Super-admin only, like every page
   * behind the gear: `ModuleSidebar` drops them for anyone else, and the gear
   * appears when the module has a settings page or a toggle.
   */
  settingsToggles?: ModuleSettingsToggle[];
}

/**
 * One switch in a module's settings gear. `element` owns its data (it fetches
 * and saves its own setting) and its own gating beyond super-admin, so the
 * module declares it once and the gear only places it.
 */
export interface ModuleSettingsToggle {
  /** Unique within the module. */
  key: string;
  element: ReactNode;
}

/**
 * The toggles a caller sees in `mod`'s settings gear: all of them for a super
 * admin, none for anyone else — like every page behind the gear.
 */
export function moduleSettingsToggles(
  mod: Pick<ModuleDefinition, "settingsToggles">,
  isSuperAdmin: boolean,
): ModuleSettingsToggle[] {
  return isSuperAdmin ? (mod.settingsToggles ?? []) : [];
}

/**
 * Marks a `ModuleSection` as holding the module's own settings.
 *
 * A section whose `key` ends with this is pulled out of the sidebar's nav
 * list and rendered behind the footer gear instead (`ModuleSettingsMenu`) —
 * see that component for why settings don't belong in the scroll list.
 *
 * A naming convention rather than a boolean on `ModuleSection` because
 * section keys must already be module-prefixed for uniqueness
 * (`energy-advisor-settings`, `operations-settings`), so the suffix is
 * information the key carries anyway. It also means a new module gets the
 * shared treatment by naming its section conventionally, with nothing to
 * opt in to and no way to declare a settings section that renders
 * inconsistently with every other module's.
 */
export const SETTINGS_SECTION_SUFFIX = "-settings";

export interface ModuleSection {
  /**
   * Globally-unique section key. See the note on uniqueness above.
   *
   * Ending it with `SETTINGS_SECTION_SUFFIX` moves the section into the
   * sidebar's footer gear instead of the nav list.
   */
  key: string;
  /** Header text. Omit for an unlabeled leading group. */
  label?: string;
  /**
   * Shared records to pin into THIS module's sidebar section, sorted in with
   * `routes`.
   *
   * For a record only one module works with: it stays a record (declared
   * once, one canonical url, no module owning the route) but appears in that
   * module's own nav rather than the product-wide `RecordBar`. Suppliers and
   * Utilities under Operations are the live examples. Records used by
   * several modules belong in `ALL_RECORDS` and the record bar instead — a
   * record in one module's sidebar reads as belonging to that module, which
   * is right here and wrong there.
   */
  records?: NavRecordKey[];
  /** The module's own pages. Rendered A–Z by label. */
  routes?: AppRoute[];
  /**
   * Replaces a `routes` row's `subItems` at RENDER time, from live data.
   *
   * For a row whose children come from the server rather than the module
   * declaration. `routes` is a module-level constant evaluated once at
   * import, so a row declaring its children there can never reflect a fetch —
   * Market Intelligence's gas "Hubs" row listed the seeded generator's eight
   * hubs (including a `Waha` and a `Dominion South` the feed does not
   * publish) no matter how many times the page reloaded, while five real gas
   * hubs were unreachable from the sidebar entirely.
   *
   * Power solved this with `recordScope.expandRows`, which is not available
   * here: gas has no record to scope to (see the module's own comment on why
   * it has no `recordScope`), so it needs the same capability without the
   * picker.
   *
   * **A hook, called in its own component.** Same constraint
   * `RecordScopeSection` documents: `ModuleSidebar` renders whichever module
   * is active, so calling this in a loop over `mod.sections` would change the
   * hook count when the user switches modules. `DynamicRowsSection` pins the
   * call to a fixed position in its own tree.
   *
   * Returning `undefined` for a path leaves that row's declared `subItems`
   * alone, which is what makes the static list a first-paint fallback rather
   * than something to delete.
   */
  useRows?: () => (path: string) => AppRoute[] | undefined;
  /**
   * Set to keep `routes` in the order written instead of sorting A–Z. For a
   * section whose items have an inherent sequence (a workflow, a
   * priority order) rather than being a flat list to scan.
   */
  preserveOrder?: boolean;
  /**
   * Marks this section's items as scoped to a selected record, with `path`
   * holding a **relative segment** rather than an absolute url.
   *
   * The sidebar resolves each into `${recordScope.basePath}/<id>/<segment>`
   * against whichever record is currently selected, and renders the record's
   * name as the section header. With nothing selected the section drops out
   * entirely — its links would have no id to point at.
   *
   * This exists because some modules are *entirely* record-scoped. Energy
   * Advisor is the case: Client Profile, Bill Analyzer, Contract Analyzer,
   * Snapshot, RFQs, and Matrix Pricing are all meaningless without a client,
   * so a static sidebar could only offer one item ("Energy Advisor") that
   * duplicated the module's name and led to a picker — with the real tools
   * stranded on a tab strip inside each client's page. Scoping the nav to
   * the selected record puts the tools where the user looks for navigation,
   * which is the Azure Portal / AWS Console resource-blade shape.
   *
   * Don't reach for this to pin a record *list* into a module's nav — that's
   * `records` above. This is for a module whose own pages only exist in the
   * context of one record.
   */
  recordScope?: RecordScope;
  /**
   * Hide this section unless the current url passes the predicate. Omitted
   * means always visible.
   *
   * For a module with more than one nav "mode" sharing one sidebar — Market
   * Intelligence's Power/Gas toggle swaps its region-scoped section for its
   * flat gas-hub section depending on which commodity the url is on, rather
   * than showing both stacked at once. Takes `pathname` AND `search` (the
   * Overview page's `?commodity=` lives in the query string, not the path) —
   * a pure function, not a hook: `ModuleSidebar` already reads `useLocation`
   * once for its own purposes, so every section's predicate runs off that
   * single read instead of each needing its own hook call.
   */
  visibleWhen?: (location: { pathname: string; search: string }) => boolean;
}

/**
 * How a `recordScope` section builds its hrefs and its header.
 *
 * Kept as data on the section rather than logic in the sidebar so the
 * mechanism generalizes: a future record-scoped module supplies its own
 * `basePath` and selection hook, and the sidebar needs no new branch.
 */
export interface RecordScope {
  /** Url prefix the id slots into, e.g. `/energy-advisor`. No trailing slash. */
  basePath: string;
  /**
   * Heading over the primary rows — which surface they belong to ("Energy
   * Advisor"). Only meaningful beside a labeled `secondary`: with two groups
   * of one record's rows, the headings say which half is the module's tools
   * and which is the record's own tabs. Unset ⇒ no heading.
   */
  label?: string;
  /**
   * A second group of rows for the same record, under a different base path,
   * rendered below a divider.
   *
   * Exists because a client's workflow spans two surfaces: the advisor tools
   * at `/energy-advisor/:id/*` (Bill Analyzer, Contract Analyzer, Snapshot,
   * RFQs) and the record's own tabs at `/customers/:id/*` (Contacts,
   * Accounts, Bills, Contracts, LOA/LOE). They're halves of one loop — you
   * upload a bill under Bills and read it under Bill Analyzer; the
   * `customer-tabs` comments say outright that create/manage lives on the
   * record page while "presentation always lives in Energy Advisor's
   * Contract Analyzer tool".
   *
   * Splitting those across two navs meant the second half was only reachable
   * by leaving the module. Listing both under the record in scope puts every
   * step of the loop one click apart.
   *
   * Rows whose `label` already appears in the primary group are dropped —
   * Client Profile renders the same component on both surfaces, so it would
   * otherwise appear twice.
   */
  secondary?: {
    /**
     * Heading over this group — which surface its rows belong to ("Client").
     * Set together with `RecordScope.label`; when both are set the two groups
     * are headed instead of split by a bare divider. Icons mode keeps the
     * divider (no room for text).
     */
    label?: string;
    /** Url prefix for this group, e.g. `/customers`. No trailing slash. */
    basePath: string;
    /** Relative segments, same shape as the section's own `routes`. */
    routes: AppRoute[];
  };
  /**
   * The record in **scope** — what the header picker displays, and what the
   * tool rows below it point at. A hook so the sidebar re-renders on change;
   * returns `null` when nothing is in scope, which drops both.
   *
   * **Allowed to outlive the route, deliberately.** The scope is chosen in
   * the picker, not by navigating, so it persists while the user visits a
   * shared record (Accounts, Clients, Deals) — and the tool rows persist with
   * it, because losing them on those pages is what stranded the user with a
   * picker and nothing under it.
   *
   * That is safe *because* the picker names the scope: a row list under a
   * picker reading "Pearl Medical Towers" is offering that client's tools,
   * not claiming you're on one of them. The earlier URL-only rule existed
   * when the section printed its own header, which did make that claim.
   */
  useScope: () => { id: string; label: string } | null;
  /** Placeholder on the picker when nothing is in scope, e.g. "Select a client". */
  pickerPlaceholder: string;
  /**
   * Which picker heads the section.
   *
   * The chrome is shared (`ScopePicker`); this chooses what is being picked —
   * the axis label, the icon, and where the options come from. Added when
   * Market Intelligence became the second record-scoped module, which is the
   * moment `RecordScopePicker` had already flagged as the time to dispatch
   * rather than grow a branch.
   *
   * Defaults to the client picker, so the Energy Advisor declaration is
   * unchanged. `gas-region` is the region picker over gas's regions.
   */
  picker?: "client" | "region" | "gas-region";
  /**
   * Drops rows that do not apply to the record in scope.
   *
   * Some scoped surfaces are data-conditional: only PJM clears a capacity
   * auction, and only some regions run a certificate program. Without this
   * the sidebar advertises a link the destination page has already filtered
   * out, which lands the user on an empty tab.
   *
   * Receives the record id and the row's relative segment. Omit it when every
   * row applies to every record, as with Energy Advisor's client tools.
   */
  filterRows?: (recordId: string, segment: string) => boolean;
  /**
   * Turns a scoped row into an expandable group with its own children.
   *
   * Market Intelligence's Hubs row lists the region's hubs, and each hub its
   * own surfaces — the sidebar then mirrors the URL all the way down instead
   * of stopping at the region. Returning `undefined` leaves the row a plain
   * link, which is what every Energy Advisor row does.
   *
   * Receives the record id and the row's relative segment; returns fully
   * resolved routes, because only the caller knows how its children nest.
   */
  expandRows?: (recordId: string, segment: string) => AppRoute[] | undefined;
  /**
   * Turns the record id into its url segment.
   *
   * Defaults to the id itself, which is right for a uuid-keyed record like a
   * client. Market Intelligence needs it because a region is identified by
   * its short code (`ISO-NE`) but addressed by a slug (`iso-ne`), and the
   * sidebar was building urls the router does not serve.
   */
  toSlug?: (recordId: string) => string;
}

/**
 * Flattens a section into `AppRoute`s, stamping the section key on each so
 * `getModuleNavSections` can group them and `routeMetaFor` can resolve
 * breadcrumbs.
 *
 * **Items sort A–Z by label** unless the section opts out with
 * `preserveOrder`. Sorting here rather than relying on declaration order
 * means every module's nav is alphabetical by construction — including those
 * whose entries still live in `APP_ROUTES` (a ~900-line array whose order
 * also drives router registration, so it can't be reordered for nav
 * cosmetics alone).
 *
 * Pinned `records` interleave alphabetically with the module's own routes
 * rather than forming a leading block — a record pinned into a module's
 * section is just another entry in that section (Suppliers in Operations sits
 * between Payees and Tasks, not above Matrix Pricing).
 * Under `preserveOrder` the written order wins, records first.
 *
 * **`recordScope` sections contribute nothing here.** Their `path`s are
 * relative segments needing a selected record's id, and resolving that needs
 * a hook — so it happens in `ModuleSidebar`'s render instead (see
 * `resolveRecordScopeSection`). Returning them raw would emit `AppRoute`s
 * with segment-only paths like `"snapshot"`, which `router.tsx` would
 * register as bogus top-level routes and `GenericLandingPage` would render as
 * broken tiles. The real routes are declared normally in `APP_ROUTES` as
 * children of the record's detail route; this section only re-presents them
 * as nav.
 */
function sectionRoutes(section: ModuleSection, moduleKey: ModuleKey): AppRoute[] {
  if (section.recordScope) return [];

  const byLabel = (a: AppRoute, b: AppRoute) => a.label.localeCompare(b.label);

  const fromRecords = (section.records ?? []).map((key) =>
    recordNavRoute(key, section.key),
  );

  const own = (section.routes ?? []).map((r) => ({
    ...r,
    section: r.section ?? section.key,
  }));

  const all = [...fromRecords, ...own];
  if (!section.preserveOrder) all.sort(byLabel);

  return all.map((r) => ({ ...r, moduleKey }) as AppRoute);
}

/** Every sidebar-visible route for a module, section order preserved. */
export function moduleNavRoutes(mod: ModuleDefinition): AppRoute[] {
  return mod.sections.flatMap((s) => sectionRoutes(s, mod.key));
}

/** Every route a module contributes to the router (nav + hidden). */
export function moduleAllRoutes(mod: ModuleDefinition): AppRoute[] {
  const hidden = (mod.hiddenRoutes ?? []).map(
    (r) => ({ ...r, moduleKey: mod.key }) as AppRoute,
  );
  return [...moduleNavRoutes(mod), ...hidden];
}

/**
 * Whether the caller may open a module — holds its access permission, is an
 * admin, or is a platform super admin and the module is `openToSuperAdmins`.
 * The one module-level gate: the switcher, launcher, sidebar and
 * `ProtectedRoute` all read it, so a module is either reachable everywhere or
 * nowhere. No `permissions` (no session) denies, the same direction
 * `isRouteAuthorized` takes.
 */
export function isModuleAuthorized(
  mod: Pick<ModuleDefinition, "requiredPermission" | "openToSuperAdmins">,
  permissions?: { granted: string[]; is_admin: boolean },
  isSuperAdmin: boolean = false,
): boolean {
  if (!permissions) return false;
  return (
    permissions.is_admin ||
    permissions.granted.includes(mod.requiredPermission) ||
    (mod.openToSuperAdmins === true && isSuperAdmin)
  );
}

/**
 * Grouped sidebar sections for one module, auth-filtered.
 *
 * The single parameterized replacement for `getNavSections` /
 * `getGlobalNavSections` / `getReportsNavSections`, which were byte-identical
 * apart from which array they walked, their section order, and their label
 * map — all three of which are now properties of the module definition.
 * `isRouteAuthorized` does the gating, unchanged.
 */
export function getModuleNavSections(
  mod: ModuleDefinition,
  isSuperAdmin: boolean = false,
  features?: SessionFeatureFlags,
  permissions?: { granted: string[]; is_admin: boolean },
  isSupplier: boolean = false,
): Array<{ section: string; label?: string; items: AppRoute[] }> {
  // A module the caller cannot open has no sidebar at all, whatever its
  // routes would individually allow.
  if (!isModuleAuthorized(mod, permissions, isSuperAdmin)) return [];
  return mod.sections
    .map((section) => {
      const items: AppRoute[] = [];
      for (const route of sectionRoutes(section, mod.key)) {
        // Parent grouping node: filter its subItems by auth. If none
        // survive, drop the parent too — an empty expander is worse than
        // nothing.
        if (route.subItems && route.subItems.length > 0) {
          if (
            !isRouteAuthorized(route, isSuperAdmin, features, permissions, isSupplier)
          ) {
            continue;
          }
          const visibleSubItems = route.subItems.filter((sub) =>
            isRouteAuthorized(sub, isSuperAdmin, features, permissions, isSupplier),
          );
          if (visibleSubItems.length === 0) continue;
          items.push({ ...route, subItems: visibleSubItems });
          continue;
        }
        if (
          !isRouteAuthorized(route, isSuperAdmin, features, permissions, isSupplier)
        ) {
          continue;
        }
        items.push(route);
      }
      return { section: section.key, label: section.label, items };
    })
    // Drop empty sections so the sidebar never renders an orphan header.
    .filter((s) => s.items.length > 0);
}

/** What a landing decision reads off the session: `isRouteAuthorized`'s inputs. */
export interface NavCaller {
  isSuperAdmin: boolean;
  isSupplier: boolean;
  features: SessionFeatureFlags;
  permissions: { granted: string[]; is_admin: boolean };
}

/** The `NavCaller` for a session. */
export function navCallerOf(session: {
  is_super_admin: boolean;
  is_supplier: boolean;
  features: SessionFeatureFlags;
  permissions: { granted: string[]; is_admin: boolean };
}): NavCaller {
  return {
    isSuperAdmin: session.is_super_admin,
    isSupplier: session.is_supplier,
    features: session.features,
    permissions: session.permissions,
  };
}

function findRoute(routes: AppRoute[], path: string): AppRoute | undefined {
  for (const r of routes) {
    if (r.path === path) return r;
    const sub = r.subItems ? findRoute(r.subItems, path) : undefined;
    if (sub) return sub;
  }
  return undefined;
}

/**
 * Where entering a module lands this caller.
 *
 * The module's `entryPath` when the caller can open it — otherwise their first
 * sidebar page, in the order the sidebar shows it. An `entryPath` behind a
 * flag or a permission the caller lacks would otherwise bounce them out of
 * the module the moment they enter it (AB#8596: Operations landed on Matrix
 * Pricing, then behind a tenant flag, so with the flag off every switch into
 * Operations ended on the dashboard). Pick the landing so it is not gated; this is what keeps a
 * gated one from locking anyone out.
 *
 * `undefined` when the caller can open nothing in the module, or cannot open
 * the module at all. No `caller` (no session to judge by) is the declared
 * `entryPath`, unjudged.
 *
 * An `entryPath` that matches no declared route (a redirect-only path) is
 * trusted: there is nothing to judge it by, and `ProtectedRoute` still guards
 * whatever it resolves to.
 */
export function moduleLandingPath(
  mod: ModuleDefinition,
  caller?: NavCaller,
): string | undefined {
  if (!caller) return mod.entryPath;
  const { isSuperAdmin, isSupplier, features, permissions } = caller;
  if (!isModuleAuthorized(mod, permissions, isSuperAdmin)) return undefined;

  const entry =
    findRoute(moduleAllRoutes(mod), mod.entryPath) ??
    findRoute(APP_ROUTES, mod.entryPath);
  if (
    !entry ||
    isRouteAuthorized(entry, isSuperAdmin, features, permissions, isSupplier)
  ) {
    return mod.entryPath;
  }

  // Record-scoped workspaces may have an entry page without a sidebar row.
  // It still needs its own permission before it can serve as the fallback.
  if (mod.fallbackEntryPath) {
    const fallback = findRoute(moduleAllRoutes(mod), mod.fallbackEntryPath);
    if (fallback && isRouteAuthorized(fallback, isSuperAdmin, features, permissions, isSupplier)) {
      return mod.fallbackEntryPath;
    }
  }

  // `getModuleNavSections` has already dropped everything the caller cannot
  // open. A cross-link is another module's page, so landing on it would be a
  // module switch; a parent with no page of its own lands on its first child.
  const sections = getModuleNavSections(
    mod,
    isSuperAdmin,
    features,
    permissions,
    isSupplier,
  );
  for (const section of sections) {
    for (const item of section.items) {
      if (item.crossLink) continue;
      const page = item.element ? item : item.subItems?.find((s) => !s.crossLink);
      if (page) return page.path;
    }
  }
  return undefined;
}
